import { BiSolidUserAccount } from "solid-icons/bi";
import { type Component, createMemo, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { LanguagesTags, SkillsTags } from "@/components/shared/tags";
import { AvatarToDeprecate } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { HeartButton } from "@/domains/company/talent-pool/components/heart-button";
import { SavedPill } from "@/domains/company/talent-pool/components/saved-pill";
import { cn } from "@/libs/cn";
import { formatDate } from "@/libs/date";
import { formatDailyRate } from "@/libs/format";
import { useCvSelection } from "../hooks/useCvSelection";
import { useFreelanceSearchStore } from "../store/freelance-search-store";
import type { FreelanceCardData } from "../types";
import { categoryLine } from "../utils/card-category";
import { ConfidenceScoreBadge } from "./ConfidenceScoreBadge";
import { MatchingProgressBar } from "./MatchingProgressBar";

type FreelanceCardProps = {
  // Structurally widened (D7): satisfied by both `FreelancerSearchResult` and the
  // pool's `PoolMatchFreelancer`, so the same card renders search + pool rows.
  freelancer: FreelanceCardData;
  onViewProfile?: () => void;
};

export const FreelanceCard: Component<FreelanceCardProps> = (props) => {
  const { t } = useI18n();
  const [store] = useFreelanceSearchStore();
  const cvSelection = useCvSelection();

  const selectionIndex = createMemo(() => {
    const idx = store.selectedFreelancers.findIndex(
      (f) => String(f.id) === String(props.freelancer.id),
    );
    return idx === -1 ? 0 : idx + 1;
  });

  /* Posizione nella selezione "CV anonimi". Gemella di `selectionIndex`: le due
     selezioni sono mutuamente esclusive (vedi `AnonymousCvButton`), quindi al
     piu' una delle due e' > 0 e la card non deve mai comporre i due stili. */
  const cvIndex = createMemo(() => {
    const idx = cvSelection
      .selected()
      .findIndex((f) => String(f.id) === String(props.freelancer.id));
    return idx === -1 ? 0 : idx + 1;
  });

  /* Riga sotto il nome: titolo e categoria, separati da " · ". La categoria
     compare anche senza titolo; l'esperienza resta legata al solo titolo. */
  const subtitle = createMemo(() => {
    const parts = [
      props.freelancer.job_title,
      categoryLine(
        props.freelancer.job_category,
        props.freelancer.job_category_secondary,
      ),
    ].filter((part): part is string => Boolean(part));
    return parts.length > 0 ? parts.join(" · ") : null;
  });

  const formatAvailableFrom = (date: Date | string | null | undefined) => {
    if (!date) return null;
    if (date instanceof Date) {
      return formatDate(date);
    }
    return formatDate(new Date(date));
  };

  return (
    <div class="relative w-full">
      <Show when={selectionIndex() > 0}>
        <div class="absolute -top-2 -left-2 z-10 h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shadow">
          {selectionIndex()}
        </div>
      </Show>
      {/* Stessa pastiglia numerata della selezione "Contatta", in viola per
          distinguere le due liste a colpo d'occhio: senza di essa la selezione
          CV si vedeva solo nella barra in alto, non sulle card scelte. */}
      <Show when={cvIndex() > 0}>
        <div
          class="absolute -top-2 -left-2 z-10 h-6 w-6 rounded-full bg-violet-600 flex items-center justify-center text-white text-xs font-bold shadow"
          title={t("company.search.freelancers.result.cv_bar.card_badge")}
        >
          {cvIndex()}
        </div>
      </Show>
      <Card
        class={cn(
          "border shadow-none bg-white rounded-lg w-full transition-[box-shadow,border-color] duration-200",
          selectionIndex() > 0 && "border-blue-600 ring-2 ring-blue-600/30",
          cvIndex() > 0 && "border-violet-600 ring-2 ring-violet-600/30",
          selectionIndex() === 0 &&
            cvIndex() === 0 &&
            "border-gray-200 hover:border-matisse-200 hover:shadow-md",
        )}
        onClick={props.onViewProfile}
      >
        <CardContent class="p-6 cursor-pointer">
          <div class="space-y-4">
            {/* Avatar */}
            <div class="flex gap-4 items-start justify-between">
              <div class="shrink-0">
                <AvatarToDeprecate
                  src={props.freelancer.avatar_url ?? ""}
                  alt={props.freelancer.full_name ?? ""}
                  class="h-16 w-16"
                  fallback={<BiSolidUserAccount class="w-8 h-8" />}
                />
              </div>
              {/* Sotto `sm` le due colonne si impilano. In riga la colonna
                  destra (tariffa, disponibilità, badge) è `shrink-0` e pesa
                  169px di min-content: su 375px lasciava 28px al nome, che
                  andava a capo una parola per riga e finiva sotto il prezzo.
                  `min-w-0` sulla riga e `items-stretch` da impilata sono
                  entrambi necessari perché `truncate` funzioni: senza il primo
                  la riga non scende sotto il proprio min-content, senza il
                  secondo la colonna si dimensiona a `fit-content`, che ha il
                  min-content come pavimento — e `truncate` porta `nowrap`,
                  quindi quel pavimento è il nome intero. */}
              <div class="w-full min-w-0 flex flex-col items-stretch justify-between gap-4 mb-3 sm:flex-row sm:items-start">
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-black mb-1 truncate">
                    {props.freelancer.full_name}
                  </h3>
                  <Show when={subtitle()}>
                    <p class="text-sm text-gray-600">{subtitle()}</p>
                    <Show
                      when={
                        props.freelancer.job_title &&
                        props.freelancer.total_experience_years
                      }
                    >
                      <div class="text-sm text-gray-600">
                        <span class="font-medium">
                          {t("company.search.freelancers.result.experience")}:{" "}
                        </span>
                        <span>
                          {props.freelancer.total_experience_years} anni
                        </span>
                      </div>
                    </Show>
                  </Show>
                </div>

                {/* Rate and Availability */}
                <div class="text-right shrink-0 flex flex-col items-end gap-1">
                  <HeartButton
                    freelancerProfileId={props.freelancer.id}
                    searchId={props.freelancer.search_id}
                  />
                  <Show when={props.freelancer.price_per_day}>
                    <p class="text-base font-medium text-primary mb-1">
                      {formatDailyRate(props.freelancer.price_per_day)}
                    </p>
                  </Show>
                  <Show
                    when={props.freelancer.available_from}
                    fallback={
                      <p class="text-sm text-gray-500">
                        {t("company.search.freelancers.result.available_now")}
                      </p>
                    }
                  >
                    {(date) => {
                      const formattedDate = formatAvailableFrom(date());
                      const text = t(
                        "company.search.freelancers.result.available_from",
                      );
                      return (
                        <p class="text-sm text-gray-500">
                          {text.replace("{{date}}", formattedDate ?? "")}
                        </p>
                      );
                    }}
                  </Show>
                  <div class="flex items-center gap-2">
                    <Show
                      when={
                        props.freelancer.confidence_score != null
                          ? { score: props.freelancer.confidence_score }
                          : undefined
                      }
                      keyed
                    >
                      {({ score: s }) => (
                        <ConfidenceScoreBadge
                          score={s}
                          breakdown={
                            props.freelancer.confidence_score_breakdown
                          }
                        />
                      )}
                    </Show>
                    <Show when={props.freelancer.already_applied}>
                      <Badge variant="success" size="sm">
                        {t("common.already_applied")}
                      </Badge>
                    </Show>
                  </div>
                </div>
              </div>

              {/* Confidence Score progress bar */}
            </div>
            <Show
              when={
                props.freelancer.confidence_score != null
                  ? { score: props.freelancer.confidence_score }
                  : undefined
              }
              keyed
            >
              {({ score: s }) => <MatchingProgressBar score={s} />}
            </Show>
            {/* Content */}
            <div class="flex-1 min-w-0">
              {/* Skills */}
              <Show
                when={
                  props.freelancer.skills && props.freelancer.skills.length > 0
                }
              >
                <div class="mb-4 flex items-center gap-2 flex-wrap">
                  <SkillsTags
                    skills={props.freelancer.skills ?? []}
                    slicedSkills={props.freelancer.skills ?? []}
                  />
                </div>
              </Show>

              {/* Actions */}
              <div class="flex gap-2 mt-4 border-t border-gray-200 pt-4">
                <div class="flex items-center gap-2 flex-wrap mb-3">
                  <SavedPill freelancerProfileId={props.freelancer.id} />
                  <Show when={props.freelancer.price_per_day}>
                    <Badge variant="info" size="sm">
                      {t("company.search.freelancers.result.price_per_day", {
                        price: props.freelancer.price_per_day!,
                      })}
                    </Badge>
                  </Show>
                  <Show when={props.freelancer.available_from}>
                    <Badge variant="secondary" size="sm">
                      {t("availability.available")}
                    </Badge>
                  </Show>
                  <Show when={props.freelancer.work_preference}>
                    {(remotePolicy) => (
                      <>
                        <Badge variant="secondary" size="sm">
                          {t(
                            "company.search.freelancers.result.work_preference",
                            {
                              // `work_preference` is now `string` (widened, D7), so
                              // this dynamic key returns `unknown`; String() narrows
                              // it back with no runtime change (i18n memory rule).
                              preference: String(
                                t(
                                  `common.remote_policy_options.${remotePolicy()}`,
                                ),
                              ),
                            },
                          )}
                        </Badge>
                        <Show when={remotePolicy() !== "remote"}>
                          <Badge variant="secondary" size="sm">
                            {t("profile.details.profileInfo.on_site")}
                          </Badge>
                        </Show>
                      </>
                    )}
                  </Show>
                  <Show when={props.freelancer.residency}>
                    <Badge variant="secondary" size="sm">
                      {t("company.search.freelancers.result.residency", {
                        residency: props.freelancer.residency!,
                      })}
                    </Badge>
                  </Show>
                  <Show when={props.freelancer.languages}>
                    <LanguagesTags languages={props.freelancer.languages!} />
                  </Show>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
